
import React from "react";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Event } from "@/types";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, MapPinIcon, UserIcon, Globe } from "lucide-react";
import { format, parseISO } from "date-fns";

interface EventDetailsDialogProps {
  event: Event | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const EventDetailsDialog = ({ event, isOpen, onOpenChange }: EventDetailsDialogProps) => {
  if (!event) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{event.title}</DialogTitle>
          <DialogDescription>
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <span>
                  {format(parseISO(event.start_date), "PPP, h:mm a")} - {format(parseISO(event.end_date), "PPP, h:mm a")}
                </span>
              </div>
              
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 text-muted-foreground" />
                  <span>{event.location}</span>
                </div>
              )}
              
              {event.organizer && (
                <div className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                  <span>{event.organizer}</span>
                </div>
              )}
              
              {event.website && (
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={event.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-eco-500 hover:underline"
                  >
                    Visit website
                  </a>
                </div>
              )}
              
              {event.category && (
                <div className="mt-2">
                  <Badge variant="outline" className="bg-eco-100 text-eco-800">
                    {event.category}
                  </Badge>
                </div>
              )}
            </div>
            
            {event.description && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm whitespace-pre-line">{event.description}</p>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailsDialog;
